import { useEffect, useRef, useState } from "react";
import { X, Lightning, LightningSlash, Images, Check, CameraRotate } from "@phosphor-icons/react";
import FieldRadarLogo from "./FieldRadarLogo";

/**
 * BurstCamera — in-app live camera that STAYS OPEN.
 * Each shutter tap = instant local thumbnail; upload runs in a background
 * queue so the next shot never waits. `allowLibrary` = staff only.
 * Tenant shots are stamped with date/time on the image itself.
 */
export default function BurstCamera({ title, initialCount = 0, onShot, onDone, onDelete, allowLibrary = false, stamp = false, itemLabel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const fileRef = useRef(null);
  const [shots, setShots] = useState([]);            // {id, url, status: 'up'|'ok'|'err'}
  const [torch, setTorch] = useState(false);
  const [torchOk, setTorchOk] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState(false);
  const queue = useRef(Promise.resolve());

  const start = async (face) => {
    stop();
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: face }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false });
      streamRef.current = s;
      const t = s.getVideoTracks()[0];
      trackRef.current = t;
      setTorchOk(!!(t.getCapabilities && t.getCapabilities().torch));
      if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play().catch(() => {}); }
      setErr("");
    } catch (e) {
      setErr("Camera blocked. Allow camera access in your browser settings, then try again.");
    }
  };
  const stop = () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; };
  useEffect(() => { start(facing); return stop; }, [facing]); // eslint-disable-line

  const toggleTorch = async () => {
    try { await trackRef.current.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(t => !t); } catch {}
  };

  const enqueue = (id, blob, takenAt) => {
    queue.current = queue.current.then(async () => {
      try { const r = await onShot(blob, { takenAt, item: itemLabel }); setShots(s => s.map(x => x.id === id ? { ...x, status: "ok", path: r?.photo?.path } : x)); }
      catch { setShots(s => s.map(x => x.id === id ? { ...x, status: "err" } : x)); }
    });
  };

  const shutter = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setFlash(true); setTimeout(() => setFlash(false), 90);
    if (navigator.vibrate) navigator.vibrate(15);
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0);
    const takenAt = new Date().toISOString();
    if (stamp) {
      const txt = new Date().toLocaleString();
      ctx.font = `${Math.round(c.width / 32)}px sans-serif`;
      ctx.fillStyle = "rgba(0,0,0,.55)";
      const w = ctx.measureText(txt).width + 24;
      ctx.fillRect(c.width - w - 12, c.height - c.width / 20 - 12, w, c.width / 20);
      ctx.fillStyle = "#fff";
      ctx.fillText(txt, c.width - w, c.height - 20);
    }
    const id = `${Date.now()}-${Math.random()}`;
    const url = c.toDataURL("image/jpeg", 0.5);
    setShots(s => [...s, { id, url, status: "up" }]);
    c.toBlob(blob => enqueue(id, blob, takenAt), "image/jpeg", 0.86);
  };

  const fromLibrary = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(f => {
      const id = `${Date.now()}-${Math.random()}`;
      setShots(s => [...s, { id, url: URL.createObjectURL(f), status: "up" }]);
      enqueue(id, f, null);
    });
    e.target.value = "";
  };

  const removeShot = async (sh) => {
    if (!window.confirm("Delete this photo?")) return;
    setShots(s => s.filter(x => x.id !== sh.id));
    if (sh.path && onDelete) onDelete(sh.path);
  };

  const total = initialCount + shots.length;
  const pending = shots.filter(s => s.status === "up").length;

  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col" data-testid="burst-camera">
      <div className="flex items-center justify-between px-4 py-3 bg-black/70 absolute top-0 inset-x-0 z-10">
        <FieldRadarLogo size={26}/>
        <button onClick={() => onDone(shots.length)} data-testid="camera-done"
          className="h-10 px-5 rounded-full bg-white/15 backdrop-blur font-bold active:bg-white/25">
          {pending ? `Done (${pending} uploading…)` : "Done"}
        </button>
      </div>

      <div className="relative flex-1 bg-black overflow-hidden">
        <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 w-full h-full object-cover"/>
        {flash && <div className="absolute inset-0 bg-white/80"/>}
        {err && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center gap-4">
            <div className="text-sm text-slate-200">{err}</div>
            <button onClick={() => start(facing)} className="h-11 px-5 rounded-full bg-teal-500 text-slate-900 font-black">Try again</button>
            {allowLibrary && <button onClick={() => fileRef.current?.click()} className="text-teal-300 underline text-sm">Choose from library instead</button>}
          </div>
        )}
        <div className="absolute bottom-3 inset-x-0 text-center text-lg font-bold drop-shadow" data-testid="camera-count">
          <span className="text-teal-400">{total}</span> photo{total === 1 ? "" : "s"} · {title}
        </div>
      </div>

      <div className="bg-[#0b1c2d]">
        <div className="flex gap-2 px-3 py-3 overflow-x-auto min-h-[104px]" data-testid="camera-thumbs">
          {shots.length === 0 && <div className="text-slate-500 text-sm self-center">Point and tap the shutter. Camera stays open.</div>}
          {shots.map((s, i) => (
            <div key={s.id} className="relative w-20 h-20 shrink-0 rounded-xl overflow-hidden border-2 border-teal-500/60">
              <img src={s.url} alt="" className="w-full h-full object-cover"/>
              <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-teal-500 text-slate-900 text-xs font-black flex items-center justify-center">{initialCount + i + 1}</span>
              {s.status === "up" && <span className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-yellow-400 animate-pulse"/>}
              {s.status === "ok" && <span className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center"><Check weight="bold" size={10}/></span>}
              {s.status === "err" && <span className="absolute bottom-1 right-1 px-1 rounded bg-red-600 text-[9px] font-black">FAILED</span>}
              {s.status !== "up" && (
                <button onClick={() => removeShot(s)} data-testid={`camera-shot-delete-${i}`} aria-label="Delete photo"
                  className="absolute top-0.5 right-0.5 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center"><X weight="bold" size={12}/></button>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-around px-6 pb-6 pt-1">
          {allowLibrary ? (
            <button onClick={() => fileRef.current?.click()} className="w-14 h-14 rounded-full flex items-center justify-center active:bg-white/10" data-testid="camera-library">
              <Images size={30}/>
            </button>
          ) : (
            <button onClick={() => setFacing(f => f === "environment" ? "user" : "environment")} className="w-14 h-14 rounded-full flex items-center justify-center active:bg-white/10" data-testid="camera-flip">
              <CameraRotate size={30}/>
            </button>
          )}
          <button onClick={shutter} disabled={!!err} data-testid="camera-shutter" aria-label="Take photo"
            className="w-24 h-24 rounded-full bg-white border-[6px] border-slate-300 active:scale-90 transition-transform disabled:opacity-30"/>
          <button onClick={toggleTorch} disabled={!torchOk} className="w-14 h-14 rounded-full flex items-center justify-center active:bg-white/10 disabled:opacity-30" data-testid="camera-torch">
            {torch ? <Lightning weight="fill" size={30} className="text-yellow-300"/> : <LightningSlash size={30}/>}
          </button>
        </div>
        <div className="text-center text-teal-400 font-bold text-sm pb-4 -mt-3">● Burst Mode{itemLabel ? ` · ${itemLabel}` : ""}</div>
      </div>
      {allowLibrary && <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={fromLibrary} data-testid="camera-library-input"/>}
      <button onClick={() => onDone(shots.length)} className="hidden"><X/></button>
    </div>
  );
}
